import { useCallback } from 'react';
import { IUseStoreURL, StoreRelativePathBuilderFn } from './types';
import useStoreInfo from './useStoreInfo';

interface IUseStoreURLProps {
  useTiendanubeUrl?: boolean;
}

/**
 * Utility hook to work with current store URLs.
 *
 * Returns the following helpers:
 * - `storeURL`: base URL of the current store.
 * - `buildAbsoluteStoreURL`: generates an absolute URL from a relative path.
 *
 * Read the helpers inline docs for more details.
 */
export function useStoreURL({
  useTiendanubeUrl = false,
}: IUseStoreURLProps = {}): IUseStoreURL {
  const { storeInfo } = useStoreInfo();
  const tiendanubeUrl = storeInfo?.tiendanubeUrl
    ? storeInfo.tiendanubeUrl
    : storeInfo?.url;
  const storeURL = useTiendanubeUrl ? tiendanubeUrl : storeInfo?.url || '';

  const buildAbsoluteStoreURL = useCallback(
    (path: string | StoreRelativePathBuilderFn) => {
      if (!storeInfo || !storeURL) {
        return '';
      }
      const relativePath = typeof path === 'string' ? path : path(storeInfo);

      try {
        return new URL(relativePath, storeURL).href;
      } catch {
        return '';
      }
    },
    [storeInfo, storeURL],
  );

  return {
    storeURL: storeURL || '',
    buildAbsoluteStoreURL,
  };
}
